import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authService, UserProfile } from '../shared/services/AuthService';
import { authGuardService } from '../shared/services/AuthGuardService';
import { AuthLoginModal } from '../components/AuthLoginModal';
import { LoginSuccessToast } from '../components/LoginSuccessToast';

interface ToastOptions {
  userName?: string;
  actionName?: string | null;
  notice?: string | null;
}

interface AuthGuardContextType {
  isAuthenticated: boolean;
  user: UserProfile;
  requireAuth: (
    action: () => void,
    options?: { actionName?: string; notice?: string; tab?: string; mode?: 'signin' | 'signup' }
  ) => boolean;
  openLoginModal: (options?: { actionName?: string; notice?: string; mode?: 'signin' | 'signup'; tab?: string }) => void;
  closeLoginModal: () => void;
  isLoginModalOpen: boolean;
  loginNotice: string | null;
  loginMode: 'signin' | 'signup';
  loginTab: string;
  triggerLoginToast: (options?: ToastOptions) => void;
}

const AuthGuardContext = createContext<AuthGuardContextType | null>(null);

export const AuthGuardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => authService.isLoggedIn());
  const [user, setUser] = useState(() => authService.getUser());

  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [loginNotice, setLoginNotice] = useState<string | null>(null);
  const [loginMode, setLoginMode] = useState<'signin' | 'signup'>('signin');
  const [loginTab, setLoginTab] = useState<string>('account');

  // Login Success Toast State
  const [toastConfig, setToastConfig] = useState<{
    isOpen: boolean;
    userName: string;
    actionName: string | null;
    notice: string | null;
  }>({
    isOpen: false,
    userName: '',
    actionName: null,
    notice: null,
  });

  const prevIsAuthenticatedRef = useRef(isAuthenticated);

  const triggerLoginToast = useCallback((options?: ToastOptions) => {
    const currentUser = authService.getUser();
    setToastConfig({
      isOpen: true,
      userName: options?.userName || currentUser.firstName || currentUser.name || 'Patron Client',
      actionName: options?.actionName || null,
      notice: options?.notice || null,
    });
  }, []);

  const closeToast = useCallback(() => {
    setToastConfig((prev) => ({ ...prev, isOpen: false }));
  }, []);

  useEffect(() => {
    const unsubscribe = authService.subscribe(() => {
      const prevAuth = prevIsAuthenticatedRef.current;
      const currentAuth = authService.isLoggedIn();
      const currentUser = authService.getUser();

      setIsAuthenticated(currentAuth);
      setUser(currentUser);

      // Trigger visual login success toast whenever guest becomes authenticated
      if (!prevAuth && currentAuth) {
        const actionDetails = authGuardService.executePendingAction();
        triggerLoginToast({
          userName: currentUser.firstName || currentUser.name || 'Patron Client',
          actionName: actionDetails?.actionName || null,
          notice: actionDetails?.notice || null,
        });
      }

      prevIsAuthenticatedRef.current = currentAuth;
    });
    return unsubscribe;
  }, [triggerLoginToast]);

  const openLoginModal = useCallback((options?: { actionName?: string; notice?: string; mode?: 'signin' | 'signup'; tab?: string }) => {
    const actionName = options?.actionName || 'Protected Action';
    if (import.meta.env.DEV) {
      console.log(`[AuthGuard] Protected action blocked for guest user: "${actionName}". Prompting authentication modal.`);
    }
    
    setLoginNotice(
      options?.notice || `Sign in or create an account to access ${actionName}.`
    );
    setLoginMode(options?.mode || 'signin');
    setLoginTab(options?.tab || 'account');
    setIsLoginModalOpen(true);
  }, []);

  const closeLoginModal = useCallback(() => {
    if (import.meta.env.DEV) {
      console.log('[AuthGuard] User canceled or closed login modal without completing authentication.');
    }
    authGuardService.clearPendingAction();
    setIsLoginModalOpen(false);
  }, []);

  const requireAuth = useCallback(
    (
      action: () => void,
      options?: { actionName?: string; notice?: string; tab?: string; mode?: 'signin' | 'signup' }
    ): boolean => {
      const actionName = options?.actionName || 'Protected Action';

      if (authService.isLoggedIn()) {
        if (import.meta.env.DEV) {
          console.log(`[AuthGuard] User authenticated. Executing action: "${actionName}".`);
        }
        action();
        return true;
      }

      if (import.meta.env.DEV) {
        console.log(`[AuthGuard] Protected action blocked for guest: "${actionName}". Intercepting and storing pending action.`);
      }
      
      authGuardService.requireAuth(action, {
        actionName,
        notice: options?.notice || `Sign in or create an account to ${actionName.toLowerCase()}.`,
        tab: options?.tab,
        mode: options?.mode,
      });

      openLoginModal({
        actionName,
        notice: options?.notice || `Sign in or create an account to ${actionName.toLowerCase()}.`,
        mode: options?.mode,
        tab: options?.tab,
      });

      return false;
    },
    [openLoginModal]
  );

  const handleLoginSuccess = useCallback(() => {
    if (import.meta.env.DEV) {
      console.log('[AuthGuard] Authentication completed successfully. Executing pending action and closing login modal.');
    }
    setIsLoginModalOpen(false);
    const actionDetails = authGuardService.executePendingAction();
    const currentUser = authService.getUser();

    triggerLoginToast({
      userName: currentUser.firstName || currentUser.name || 'Patron Client',
      actionName: actionDetails?.actionName || null,
      notice: actionDetails?.notice || loginNotice,
    });
  }, [triggerLoginToast, loginNotice]);

  return (
    <AuthGuardContext.Provider
      value={{
        isAuthenticated,
        user,
        requireAuth,
        openLoginModal,
        closeLoginModal,
        isLoginModalOpen,
        loginNotice,
        loginMode,
        loginTab,
        triggerLoginToast,
      }}
    >
      {children}
      <AuthLoginModal
        isOpen={isLoginModalOpen}
        onClose={closeLoginModal}
        onSuccess={handleLoginSuccess}
        notice={loginNotice}
        initialMode={loginMode}
      />
      <LoginSuccessToast
        isOpen={toastConfig.isOpen}
        onClose={closeToast}
        userName={toastConfig.userName}
        actionName={toastConfig.actionName}
        notice={toastConfig.notice}
      />
    </AuthGuardContext.Provider>
  );
};

export const useAuthGuard = () => {
  const context = useContext(AuthGuardContext);
  if (!context) {
    throw new Error('useAuthGuard must be used within an AuthGuardProvider');
  }
  return context;
};

interface ProtectedActionProps {
  actionName: string;
  onAction: () => void;
  notice?: string;
  children: React.ReactNode;
  className?: string;
  tab?: string;
  mode?: 'signin' | 'signup';
}

export const ProtectedAction: React.FC<ProtectedActionProps> = ({
  actionName,
  onAction,
  notice,
  children,
  className,
  tab,
  mode,
}) => {
  const { requireAuth } = useAuthGuard();

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    requireAuth(onAction, { actionName, notice, tab, mode });
  };

  return (
    <div className={className} onClick={handleClick}>
      {children}
    </div>
  );
};
